/**
* Cookie Consent Management Module
* Handles cookie consent dialog and user preferences
*/

class CookieConsentManager
{
    constructor()
    {
        this.consentKey = 'cookie_consent';
        this.preferencesKey = 'cookie_preferences';
        this.hasShownConsent = this.hasConsentBeenShown();
        this.userPreferences = this.getUserPreferences();

        // Define cookie categories
        this.cookieCategories = {
            necessary:
            {
                name: 'Notwendige Cookies',
                description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
                required: true,
                cookies: ['authToken', 'currentUser', 'wasOnDashboard', 'cookie_consent', 'cookie_preferences']
            },
            analytics:
            {
                name: 'Analyse-Cookies',
                description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.',
                required: false,
                cookies: ['analytics_session', 'page_views', 'user_interactions']
            },
            functional:
            {
                name: 'Funktionale Cookies',
                description: 'Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung.',
                required: false,
                cookies: ['language_preference', 'theme_preference', 'cart_data']
            },
            marketing:
            {
                name: 'Marketing-Cookies',
                description: 'Diese Cookies werden verwendet, um relevante Werbung anzuzeigen.',
                required: false,
                cookies: ['marketing_tracking', 'ad_preferences', 'campaign_data']
            }
        };

        this.init();
    }

    /**
    * Initialize cookie consent management
    */
    init()
    {
        if (!this.hasShownConsent)
        {
            this.showConsentDialog();
        }
        else
        {
            this.applyUserPreferences();
        }

        // Create persistent settings icon - DISABLED: icon removed from lower left corner
        // this.createSettingsIcon();
    }

    /**
    * Check if consent has been shown before
    */
    hasConsentBeenShown()
    {
        return localStorage.getItem(this.consentKey) === 'true';
    }

    /**
    * Get user cookie preferences
    */
    getUserPreferences()
    {
        const stored = localStorage.getItem(this.preferencesKey);
        if (stored)
        {
            try
            {
                return JSON.parse(stored);
            }
            catch (e)
            {
                console.warn('Failed to parse cookie preferences:', e);
            }
        }

        // Default preferences (only necessary cookies)
        return {
            necessary: true,
            analytics: false,
            functional: false,
            marketing: false
        };
    }

    /**
    * Save user preferences
    */
    saveUserPreferences(preferences)
    {
        this.userPreferences = preferences;
        localStorage.setItem(this.preferencesKey, JSON.stringify(preferences));
        localStorage.setItem(this.consentKey, 'true');
        this.applyUserPreferences();
    }

    /**
    * Apply user preferences to cookie management
    */
    applyUserPreferences()
    {
        // Remove cookies that user has not consented to
        Object.keys(this.cookieCategories).forEach(category =>
        {
            if (!this.userPreferences[category])
            {
                this.cookieCategories[category].cookies.forEach(cookieName =>
                {
                    this.deleteCookie(cookieName);
                });
            }
        });

        // Update cookie settings based on preferences
        this.updateCookieSettings();
    }

    /**
    * Delete a cookie
    */
    deleteCookie(name)
    {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    }

    /**
    * Update cookie settings based on user preferences
    */
    updateCookieSettings()
    {
        // Disable analytics if not consented
        if (!this.userPreferences.analytics)
        {
            // Disable Google Analytics or other analytics tools
            if (typeof gtag !== 'undefined')
            {
                gtag('consent', 'update', {
                    'analytics_storage': 'denied'
                });
            }
        }

        // Disable marketing cookies if not consented
        if (!this.userPreferences.marketing)
        {
            // Disable marketing tracking
            if (typeof gtag !== 'undefined')
            {
                gtag('consent', 'update', {
                    'ad_storage': 'denied'
                });
            }
        }
    }

    /**
    * Show the cookie consent dialog as a blocking banner
    */
    showConsentDialog()
    {
        // Create blocking overlay
        const modal = document.createElement('div');
        modal.id = 'cookieConsentModal';
        modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.8);
        z-index: 10000;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding-bottom: 0;
        box-sizing: border-box;
        `;

        const bannerContent = document.createElement('div');
        bannerContent.style.cssText = `
        background: white;
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
        padding: 1.5rem;
        max-width: 800px;
        width: 100%;
        max-height: 50vh;
        overflow-y: auto;
        box-shadow: 0 -8px 32px rgba(0,0,0,0.3);
        animation: slideUp 0.3s ease-out;
        outline: none;
        `;

        // Add CSS animation
        const style = document.createElement('style');
        style.textContent = `
        @keyframes slideUp {
            from {
                transform: translateY(100%);
                opacity: 0;
            }
            to {
                transform: translateY(0);
                opacity: 1;
            }
        }
        `;
        document.head.appendChild(style);

        bannerContent.innerHTML = this.generateConsentDialogHTML();

        modal.appendChild(bannerContent);
        document.body.appendChild(modal);
        
        // Prevent pointer events on backdrop when detailed selection is active
        modal.style.pointerEvents = 'auto';
        modal.setAttribute('data-detailed-selection', 'false');

        // Add event listeners
        this.addConsentDialogListeners(modal);

        // Lock page scroll
        if (window.UIUtils && window.UIUtils.lockPageScroll)
        {
            window.UIUtils.lockPageScroll();
        }
    }

    /**
    * Generate HTML for the consent dialog
    */
    generateConsentDialogHTML()
    {
        return `
        <div style="text-align: center; margin-bottom: 1.5rem;">
        <h2 style="color: #667eea; margin: 0 0 0.5rem 0;">🍪 Cookie-Einstellungen</h2>
        <p style="color: #666; margin: 0; font-size: 0.95rem;">
        Wir verwenden Cookies, um Ihre Erfahrung zu verbessern. Wählen Sie aus, welche Cookies Sie zulassen möchten.
        </p>
        </div>

        <div id="cookieCategoriesContainer" style="margin-bottom: 2rem; display: none;">
        <!-- Cookie categories will be populated here -->
        </div>

        <div style="display: flex; gap: 1rem; margin-top: 2rem;">
        <button type="button" id="cookieAcceptAll" style="flex: 1; background: #667eea; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">
        Alle akzeptieren
        </button>
        <button type="button" id="cookieRejectAll" style="flex: 1; background: #667eea; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">
        Alle ablehnen
        </button>
        <button type="button" id="cookieSelectButton" style="flex: 1; background: #667eea; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">
        Auswählen
        </button>
        </div>

        <div style="text-align: center; margin-top: 1rem;">
        <a href="#" id="cookieMoreInfo" style="color: #667eea; text-decoration: none; font-size: 0.9rem;">
        Mehr Informationen
        </a>
        </div>
        `;
    }

    /**
    * Add event listeners to the consent dialog
    */
    addConsentDialogListeners(modal)
    {
        // Accept all cookies
        document.getElementById('cookieAcceptAll').addEventListener('click', () =>
        {
            const allAccepted = {
                necessary: true,
                analytics: true,
                functional: true,
                marketing: true
            };
            this.saveUserPreferences(allAccepted);
            this.closeConsentDialog(modal);
        });

        // Reject all cookies (except necessary)
        document.getElementById('cookieRejectAll').addEventListener('click', () =>
        {
            const onlyNecessary = {
                necessary: true,
                analytics: false,
                functional: false,
                marketing: false
            };
            this.saveUserPreferences(onlyNecessary);
            this.closeConsentDialog(modal);
        });

        // Show/hide cookie selection or save preferences
        document.getElementById('cookieSelectButton').addEventListener('click', () =>
        {
            const button = document.getElementById('cookieSelectButton');
            const categoriesContainer = document.getElementById('cookieCategoriesContainer');
            // Find the banner content (the white dialog box)
            const bannerContent = modal.firstElementChild;

            if (categoriesContainer.style.display === 'none')
            {
                // Show cookie categories
                this.populateCookieCategories();
                categoriesContainer.style.display = 'block';
                button.textContent = 'Auswahl speichern';
                
                // Focus the dialog content and prevent backdrop interaction
                if (bannerContent)
                {
                    bannerContent.setAttribute('tabindex', '-1');
                    // Use requestAnimationFrame to ensure DOM is ready before focusing
                    requestAnimationFrame(() => {
                        bannerContent.focus();
                    });
                }
                
                // Mark that detailed selection is active
                modal.setAttribute('data-detailed-selection', 'true');
                
                // Prevent all pointer events on backdrop
                modal.style.pointerEvents = 'auto';
                const backdrop = modal;
                backdrop.style.cursor = 'default';
            }
            else
            {
                // Save custom preferences
                const preferences = {};
                Object.keys(this.cookieCategories).forEach(categoryKey =>
                {
                    const checkbox = document.getElementById(`cookie_${categoryKey}`);
                    preferences[categoryKey] = checkbox.checked;
                });
                this.saveUserPreferences(preferences);
                this.closeConsentDialog(modal);
            }
        });

        // More information link
        document.getElementById('cookieMoreInfo').addEventListener('click', (e) =>
        {
            e.preventDefault();
            this.showCookieDetails();
        });

        // Close dialog when clicking outside - default to accept all
        // But prevent closing when detailed cookie selection is active
        modal.addEventListener('click', (e) =>
        {
            if (e.target === modal)
            {
                // Don't allow closing when detailed selection is active
                if (modal.getAttribute('data-detailed-selection') === 'true')
                {
                    e.stopPropagation();
                    return;
                }
                
                // If dialog is closed without making a choice, accept all cookies
                const allAccepted = {
                    necessary: true,
                    analytics: true,
                    functional: true,
                    marketing: true
                };
                this.saveUserPreferences(allAccepted);
                this.closeConsentDialog(modal);
            }
        });
        
        // Prevent all interaction on the backdrop when detailed selection is active
        const preventBackdropInteraction = (e) =>
        {
            if (modal.getAttribute('data-detailed-selection') === 'true' && e.target === modal)
            {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };
        
        // Prevent scrolling on the backdrop when detailed selection is active
        modal.addEventListener('wheel', preventBackdropInteraction, { passive: false });
        
        // Prevent touch scrolling on the backdrop when detailed selection is active
        modal.addEventListener('touchmove', preventBackdropInteraction, { passive: false });
        
        // Prevent mouse down/up on the backdrop when detailed selection is active
        modal.addEventListener('mousedown', preventBackdropInteraction);
        modal.addEventListener('mouseup', preventBackdropInteraction);
        
        // Prevent context menu on the backdrop when detailed selection is active
        modal.addEventListener('contextmenu', preventBackdropInteraction);

        // Close dialog on Escape key
        const escapeHandler = (e) =>
        {
            if (e.key === 'Escape' && document.getElementById('cookieConsentModal'))
            {
                // If dialog is closed without making a choice, accept all cookies
                const allAccepted = {
                    necessary: true,
                    analytics: true,
                    functional: true,
                    marketing: true
                };
                this.saveUserPreferences(allAccepted);
                document.removeEventListener('keydown', escapeHandler);
                this.closeConsentDialog(modal);
            }
        };
        document.addEventListener('keydown', escapeHandler);
        // Store handler reference on modal for cleanup
        modal._escapeHandler = escapeHandler;
    }

    /**
    * Populate cookie categories in the container
    */
    populateCookieCategories()
    {
        const container = document.getElementById('cookieCategoriesContainer');
        if (!container)
        {
            return;
        }

        let categoriesHTML = '';

        Object.keys(this.cookieCategories).forEach(categoryKey =>
        {
            const category = this.cookieCategories[categoryKey];
            const isChecked = category.required || this.userPreferences[categoryKey];
            const isDisabled = category.required;
            const disabled = isDisabled ? 'disabled' : '';
            const sliderCursor = isDisabled ? 'not-allowed' : 'pointer';
            const sliderPointerEvents = isDisabled ? 'none' : 'auto';

            categoriesHTML += `
            <div class="cookie-category" style="margin-bottom: 1rem; padding: 1.5rem; border: 1px solid #e0e0e0; border-radius: 8px; background: ${isChecked ? '#f0f7ff' : '#f5f5f5'};">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
            <label style="font-weight: 600; color: #333; cursor: ${isDisabled ? 'default' : 'pointer'}; flex: 1;">
            ${category.name}
            ${category.required ? '<span style="color: #666; font-size: 0.9rem;"> (Erforderlich)</span>' : ''}
            </label>
            <label class="toggle-switch" style="position: relative; display: inline-block; width: 50px; height: 28px;">
            <input type="checkbox" id="cookie_${categoryKey}" ${isChecked ? 'checked' : ''} ${disabled} 
            style="opacity: 0; width: 0; height: 0; position: absolute;">
            <span class="slider" style="position: absolute; cursor: ${sliderCursor}; pointer-events: ${sliderPointerEvents}; top: 0; left: 0; right: 0; bottom: 0; background-color: ${isChecked ? '#667eea' : '#ccc'}; transition: 0.3s; border-radius: 28px; z-index: 1;">
            <span style="position: absolute; content: ''; height: 22px; width: 22px; left: 3px; bottom: 3px; background-color: white; transition: 0.3s; border-radius: 50%; transform: ${isChecked ? 'translateX(22px)' : 'translateX(0)'};"></span>
            </span>
            </label>
            </div>
            <p style="margin: 0; color: #666; font-size: 0.9rem; line-height: 1.4;">
            ${category.description}
            </p>
            </div>
            `;
        });

        container.innerHTML = categoriesHTML;

        // Add event listeners for toggle switches
        Object.keys(this.cookieCategories).forEach(categoryKey =>
        {
            const category = this.cookieCategories[categoryKey];
            const checkbox = document.getElementById(`cookie_${categoryKey}`);
            const slider = checkbox ? checkbox.nextElementSibling : null;
            const settingDiv = checkbox ? checkbox.closest('.cookie-category') : null;

            if (checkbox)
            {
                // Explicitly ensure checkbox is enabled for non-required categories
                if (!category.required)
                {
                    checkbox.disabled = false;
                    if (slider)
                    {
                        slider.style.pointerEvents = 'auto';
                        slider.style.cursor = 'pointer';
                    }
                }

                // Add click handler to toggle switch
                checkbox.addEventListener('change', function()
                {
                    const isChecked = this.checked;
                    if (slider)
                    {
                        slider.style.backgroundColor = isChecked ? '#667eea' : '#ccc';
                        const sliderSpan = slider.querySelector('span');
                        if (sliderSpan)
                        {
                            sliderSpan.style.transform = isChecked ? 'translateX(22px)' : 'translateX(0)';
                        }
                    }
                    if (settingDiv)
                    {
                        settingDiv.style.background = isChecked ? '#f0f7ff' : '#f5f5f5';
                    }
                });

                // Also allow clicking on the slider (only for enabled checkboxes)
                if (slider && !checkbox.disabled)
                {
                    slider.addEventListener('click', (e) =>
                    {
                        e.preventDefault();
                        e.stopPropagation();
                        checkbox.checked = !checkbox.checked;
                        checkbox.dispatchEvent(new Event('change'));
                    });
                }
            }
        });
    }

    /**
    * Close the consent dialog
    */
    closeConsentDialog(modal)
    {
        // Remove escape key handler if it exists
        if (modal && modal._escapeHandler)
        {
            document.removeEventListener('keydown', modal._escapeHandler);
        }

        modal.remove();

        // Unlock page scroll - ensure scrolling is always restored
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
        }
        else
        {
            // Fallback: manually restore scrolling if UIUtils is not available
            const body = document.body;
            body.style.position = '';
            body.style.top = '';
            body.style.width = '';
            body.style.overflow = '';
            document.documentElement.style.overflow = '';
        }

        // Show confirmation message
        if (window.UIUtils && window.UIUtils.showNotification)
        {
            window.UIUtils.showNotification('Cookie-Einstellungen gespeichert', 'success');
        }
    }

    /**
    * Show detailed cookie information
    */
    showCookieDetails()
    {
        const detailsModal = document.createElement('div');
        detailsModal.id = 'cookieDetailsModal';
        detailsModal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.8);
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        box-sizing: border-box;
        `;

        const detailsContent = document.createElement('div');
        detailsContent.style.cssText = `
        background: white;
        border-radius: 12px;
        padding: 2rem;
        max-width: 700px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        `;

        // Create close button function
        const closeDetailsModal = () =>
        {
            detailsModal.remove();
            // Re-focus on the cookie consent modal by bringing it forward
            const cookieModal = document.getElementById('cookieConsentModal');
            if (cookieModal)
            {
                cookieModal.style.zIndex = '10000';
            }
            if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
            {
                window.UIUtils.updateGlobalCloseButton();
            }
        };

        let detailsHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
        <h2 style="color: #667eea; margin: 0;">🍪 Cookie-Details</h2>
        </div>
        `;

        Object.keys(this.cookieCategories).forEach(categoryKey =>
        {
            const category = this.cookieCategories[categoryKey];
            detailsHTML += `
            <div style="margin-bottom: 2rem; padding: 1rem; border: 1px solid #e0e0e0; border-radius: 8px;">
            <h3 style="color: #333; margin: 0 0 0.5rem 0;">${category.name}</h3>
            <p style="color: #666; margin: 0 0 1rem 0;">${category.description}</p>
            <div>
            <strong>Verwendete Cookies:</strong>
            <ul style="margin: 0.5rem 0; padding-left: 1.5rem;">
            ${category.cookies.map(cookie => `<li style="color: #666; font-size: 0.9rem;">${cookie}</li>`).join('')}
            </ul>
            </div>
            </div>
            `;
        });

        detailsContent.innerHTML = detailsHTML;
        detailsModal.appendChild(detailsContent);
        document.body.appendChild(detailsModal);

        detailsModal._closeModal = closeDetailsModal;
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            requestAnimationFrame(() => window.UIUtils.updateGlobalCloseButton());
        }

        // Close when clicking outside
        detailsModal.addEventListener('click', (e) =>
        {
            if (e.target === detailsModal)
            {
                closeDetailsModal();
            }
        });
    }

    /**
    * Check if a specific cookie category is allowed
    */
    isCategoryAllowed(category)
    {
        return this.userPreferences[category] === true;
    }

    /**
    * Set a cookie with consent check
    */
    setCookie(name, value, days = 30, category = 'functional')
    {
        if (this.isCategoryAllowed(category))
        {
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
            document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
            return true;
        }
        return false;
    }

    /**
    * Get current cookie preferences for external use
    */
    getPreferences()
    {
        return this.userPreferences;
    }

    /**
    * Create a persistent settings icon in the bottom-left corner
    */
    createSettingsIcon()
    {
        // Remove existing icon container if any
        const existingIconContainer = document.getElementById('gdprSettingsIconContainer');
        if (existingIconContainer)
        {
            existingIconContainer.remove();
        }

        // Create the icon container
        const iconContainer = document.createElement('div');
        iconContainer.id = 'gdprSettingsIconContainer';
        iconContainer.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 20px;
        display: flex;
        align-items: center;
        gap: 0px;
        z-index: 1000;
        `;

        // Create the icon
        const icon = document.createElement('div');
        icon.id = 'gdprSettingsIcon';
        icon.innerHTML = '🍪';
        icon.style.cssText = `
        width: 50px;
        height: 50px;
        background: #d3d3d3;
        color: #333;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        transition: all 0.3s ease;
        position: relative;
        z-index: 2;
        `;

        // Create label
        const label = document.createElement('span');
        label.textContent = 'Datenschutzeinstellungen';
        label.style.cssText = `
        background: #d3d3d3;
        color: #333;
        height: 50px;
        padding: 0 20px 0 12px;
        border-radius: 25px;
        font-size: 14px;
        font-weight: 500;
        box-shadow: none;
        white-space: nowrap;
        opacity: 0;
        width: 0;
        overflow: hidden;
        pointer-events: none;
        margin-left: 0;
        display: none;
        align-items: center;
        justify-content: flex-end;
        vertical-align: middle;
        z-index: 1;
        line-height: 50px;
        position: absolute;
        left: 100px;
        top: 0;
        transition: opacity 0.3s ease-out, width 0.5s ease-out;
        `;

        iconContainer.appendChild(icon);
        iconContainer.appendChild(label);

        // Show label when hovering over icon or label
        const showLabel = () =>
        {
            // Position label to start at the same left position as icon
            label.style.left = '0';
            // Show and roll out animation - expand width
            label.style.display = 'flex';
            label.style.opacity = '1';
            label.style.width = 'auto';
            label.style.minWidth = '290px';
        };

        // Hide label when not hovering over icon or label
        const hideLabel = () =>
        {
            // Roll in animation - collapse width then hide
            label.style.width = '0';
            label.style.opacity = '0';
            setTimeout(() =>
            {
                label.style.display = 'none';
            }, 500);
        };

        // Track if we should show the label
        let isHovering = false;
        let hideTimeout = null;

        // Show label when hovering over container
        iconContainer.addEventListener('mouseenter', () =>
        {
            isHovering = true;
            if (hideTimeout)
            {
                clearTimeout(hideTimeout);
                hideTimeout = null;
            }
            showLabel();
        });

        // Hide label when mouse leaves the container
        iconContainer.addEventListener('mouseleave', () =>
        {
            isHovering = false;
            if (hideTimeout) clearTimeout(hideTimeout);
            hideTimeout = setTimeout(() =>
            {
                if (!isHovering)
                {
                    hideLabel();
                }
            }, 300);
        });

        // Add click event to icon only
        icon.addEventListener('click', () =>
        {
            this.openGPDRSettings();
        });

        document.body.appendChild(iconContainer);
    }

    /**
    * Open GDPR settings dialog
    */
    openGPDRSettings()
    {
        // Cache window.innerWidth once to avoid multiple forced reflows
        const isMobile = window.innerWidth <= 768;
        
        // Create blocking overlay
        const modal = document.createElement('div');
        modal.id = 'gdprSettingsModal';
        modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${isMobile ? '#000000' : 'rgba(0,0,0,0.8)'}; /* Fully opaque on mobile to hide content below */
        z-index: ${isMobile ? '10001' : '11000'}; /* Above header (10000) on desktop */
        display: flex;
        align-items: ${isMobile ? 'center' : 'flex-end'}; /* Centered on mobile, bottom on desktop */
        justify-content: center;
        padding: ${isMobile ? '1rem' : '0'};
        padding-bottom: ${isMobile ? '1rem' : '0'};
        box-sizing: border-box;
        `;

        const modalContent = document.createElement('div');
        modalContent.style.cssText = `
        background: white;
        border-radius: ${isMobile ? '15px' : '20px 20px 0 0'}; /* Rounded on all sides on mobile, top only on desktop */
        padding: ${isMobile ? '1.5rem' : '2rem'};
        max-width: ${isMobile ? 'calc(100vw - 2rem)' : '800px'};
        width: ${isMobile ? 'calc(100% - 2rem)' : '100%'};
        max-height: 85vh;
        overflow-y: auto;
        box-shadow: ${isMobile ? '0 20px 40px rgba(0,0,0,0.3)' : '0 -8px 32px rgba(0,0,0,0.3)'};
        animation: ${isMobile ? 'none' : 'slideUp 0.3s ease-out'};
        `;

        let contentHTML = `
        <div style="text-align: center; margin-bottom: 1.5rem;">
        <h2 style="color: #667eea; margin: 0 0 0.5rem 0;">🔒 GDPR-Einstellungen</h2>
        <p style="color: #666; margin: 0; font-size: 0.95rem;">
        Verwalten Sie Ihre Datenschutzeinstellungen
        </p>
        </div>

        <div style="margin-bottom: 2rem;">
        `;

        // Add toggle switches for each category (except necessary)
        Object.keys(this.cookieCategories).forEach(categoryKey =>
        {
            const category = this.cookieCategories[categoryKey];
            if (category.required)
            {
                return; // Skip necessary cookies
            }

            const isEnabled = this.userPreferences[categoryKey];

            contentHTML += `
            <div class="gdpr-setting" style="margin-bottom: 1.5rem; padding: 1.5rem; border: 1px solid #e0e0e0; border-radius: 8px; background: ${isEnabled ? '#f0f7ff' : '#f5f5f5'};">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
            <label style="font-weight: 600; color: #333; cursor: pointer; flex: 1;">
            ${category.name}
            </label>
            <label class="toggle-switch" style="position: relative; display: inline-block; width: 50px; height: 28px; cursor: pointer;">
            <input type="checkbox" id="gdpr_${categoryKey}" ${isEnabled ? 'checked' : ''} 
            style="position: absolute; opacity: 0; width: 50px; height: 28px; margin: 0; cursor: pointer; z-index: 2;">
            <span class="slider" style="position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: ${isEnabled ? '#667eea' : '#ccc'}; transition: 0.3s; border-radius: 28px; z-index: 1;">
            <span style="position: absolute; content: ''; height: 22px; width: 22px; left: 3px; bottom: 3px; background-color: white; transition: 0.3s; border-radius: 50%; transform: ${isEnabled ? 'translateX(22px)' : 'translateX(0)'};"></span>
            </span>
            </label>
            </div>
            <p style="margin: 0; color: #666; font-size: 0.9rem; line-height: 1.4;">
            ${category.description}
            </p>
            </div>
            `;
        });

        contentHTML += `
        </div>

        <div style="display: flex; gap: 1rem;">
        <button type="button" id="gdprSaveBtn" style="flex: 1; background: #667eea; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">
        Einstellungen speichern
        </button>
        <button type="button" id="gdprCancelBtn" style="flex: 1; background: #6c757d; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">
        Abbrechen
        </button>
        </div>
        `;

        modalContent.innerHTML = contentHTML;
        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        // Add CSS for toggle switches
        const style = document.createElement('style');
        style.textContent = `
        .toggle-switch input[type="checkbox"]:checked + .slider {
            background-color: #667eea !important;
        }
        .toggle-switch input[type="checkbox"]:checked + .slider span {
            transform: translateX(22px) !important;
        }
        .toggle-switch input[type="checkbox"]:not(:checked) + .slider {
            background-color: #ccc !important;
        }
        .toggle-switch input[type="checkbox"]:not(:checked) + .slider span {
            transform: translateX(0) !important;
        }
        `;
        document.head.appendChild(style);

        // Add event listeners for toggle switches
        Object.keys(this.cookieCategories).forEach(categoryKey =>
        {
            const category = this.cookieCategories[categoryKey];
            if (category.required)
            {
                return;
            }

            const checkbox = document.getElementById(`gdpr_${categoryKey}`);
            const slider = checkbox.nextElementSibling;
            const settingDiv = checkbox.closest('.gdpr-setting');

            if (checkbox)
            {
                // Add click handler to toggle switch
                checkbox.addEventListener('change', function()
                {
                    const isChecked = this.checked;
                    slider.style.backgroundColor = isChecked ? '#667eea' : '#ccc';
                    slider.querySelector('span').style.transform = isChecked ? 'translateX(22px)' : 'translateX(0)';
                    settingDiv.style.background = isChecked ? '#f0f7ff' : '#f5f5f5';
                });

                // Also allow clicking on the slider span
                if (slider)
                {
                    slider.addEventListener('click', () =>
                    {
                        checkbox.checked = !checkbox.checked;
                        checkbox.dispatchEvent(new Event('change'));
                    });
                }
            }
        });

        // Add event listeners
        document.getElementById('gdprSaveBtn').addEventListener('click', () =>
        {
            const preferences = {};
            Object.keys(this.cookieCategories).forEach(categoryKey =>
            {
                const checkbox = document.getElementById(`gdpr_${categoryKey}`);
                if (checkbox)
                {
                    preferences[categoryKey] = checkbox.checked;
                }
                else
                {
                    preferences[categoryKey] = this.cookieCategories[categoryKey].required || this.userPreferences[categoryKey];
                }
            });
            this.saveUserPreferences(preferences);
            this.closeGPDRSettings(modal, true); // Pass true to indicate settings were saved
        });

        document.getElementById('gdprCancelBtn').addEventListener('click', () =>
        {
            this.closeGPDRSettings(modal, false); // Pass false to indicate cancel
        });

        // Close when clicking outside
        modal.addEventListener('click', (e) =>
        {
            if (e.target === modal)
            {
                this.closeGPDRSettings(modal, false); // Pass false for cancel
            }
        });

        // Close on Escape key
        const escapeHandler = (e) =>
        {
            if (e.key === 'Escape' && document.getElementById('gdprSettingsModal'))
            {
                document.removeEventListener('keydown', escapeHandler);
                this.closeGPDRSettings(modal, false);
            }
        };
        document.addEventListener('keydown', escapeHandler);
        // Store handler reference on modal for cleanup
        modal._escapeHandler = escapeHandler;

        // Lock page scroll
        if (window.UIUtils && window.UIUtils.lockPageScroll)
        {
            window.UIUtils.lockPageScroll();
        }
    }

    closeGPDRSettings(modal, saved = false)
    {
        // Remove escape key handler if it exists
        if (modal && modal._escapeHandler)
        {
            document.removeEventListener('keydown', modal._escapeHandler);
        }

        modal.remove();

        // Unlock page scroll - ensure scrolling is always restored
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
        }
        else
        {
            // Fallback: manually restore scrolling if UIUtils is not available
            const body = document.body;
            body.style.position = '';
            body.style.top = '';
            body.style.width = '';
            body.style.overflow = '';
            document.documentElement.style.overflow = '';
        }

        // Show confirmation message only if settings were saved
        if (saved && window.UIUtils && window.UIUtils.showNotification)
        {
            window.UIUtils.showNotification('GDPR-Einstellungen aktualisiert', 'success');
        }
    }
}

// Initialize cookie consent manager when DOM is loaded
document.addEventListener('DOMContentLoaded', () =>
{
    window.CookieConsentManager = new CookieConsentManager();
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports)
{
    module.exports = CookieConsentManager;
}
